import React, { useEffect, useRef, useState } from "react";
import {
  cleanSpecialCharacters,
  parseFloatToFixed,
  translateDiscountType,
} from "../../../../extras";

import { ORDER_WITH_FULL_PAYMENTS } from "../../../../types/alerts";
import { dispatchAddAlert } from "../../../../utils/alerts";

// FRAMER MOTION
import { AnimatePresence, motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

// ICONS
import {
  AddCircle as AddCircleIcon,
  ArrowBack as ArrowBackIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  Discount as DiscountIcon,
  ExpandMore as ExpandMoreIcon,
  LocalAtm as LocalAtmIcon,
  Pix as PixIcon,
} from "@mui/icons-material";

// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  addDiscount,
  addPaymentMethod,
  removeDiscount,
  removePaymentMethod,
  setApplyServiceFee,
  setPaymentMethods,
  setShowPaymentForm,
} from "../../../../store/actions/newOrderSlice";

// COMPONENTS
import ConfirmModal from "../../../modals/confirmModal";
import AddPaymentMethodModal from "../../modal/NewOrderModal/Modals/addPaymentMethodModal";
import AddDiscountModal from "../../modal/NewOrderModal/Modals/addDiscountModal";
import { formatFinance } from "../../../../utils/formatters";

// WINDOWS 11 STYLED COMPONENTS
import {
  PaymentSummaryPanel,
  PaymentMethodChip,
  DiscountChip,
  AddActionChip,
  LinearPaymentProgress,
  ServiceFeeToggleChip,
  SectionContainer,
} from "../../../styled/pos/windows11";

const getPaymentMethodIcon = (methodValue) => {
  const iconProps = { sx: { fontSize: 20 } };

  switch (methodValue) {
    case "DINHEIRO":
      return <LocalAtmIcon {...iconProps} />;
    case "PIX":
      return <PixIcon {...iconProps} />;
    case "CREDITO":
    case "DEBITO":
    case "VALE_ALIMENTACAO":
    case "VALE_REFEICAO":
      return <CreditCardIcon {...iconProps} />;
    default:
      return <AttachMoneyIcon {...iconProps} />;
  }
};

export default function FormFinance({
  allowDiscountEdit = true,
  isEdit = false,
  onCreateOrder,
}) {
  const dispatch = useDispatch();

  const total = useSelector((state) => state.newOrder.total);
  const totalToPay = useSelector((state) => state.newOrder.totalToPay);
  const itemsTotal = useSelector((state) => state.newOrder.itemsTotal);
  const discounts = useSelector((state) => state.newOrder.discounts);
  const discountsValue = useSelector((state) => state.newOrder.discountsValue);
  const serviceFee = useSelector((state) => state.newOrder.serviceFee);
  const serviceFeeAmount = useSelector(
    (state) => state.newOrder.serviceFeeAmount
  );
  const applyServiceFee = useSelector(
    (state) => state.newOrder.applyServiceFee
  );
  const paymentMethods = useSelector((state) => state.newOrder.paymentMethods);
  const orderType = useSelector((state) => state.newOrder.orderType);
  const newOrderTable = useSelector((state) => state.orders.newOrderTable);
  const isTableOrder = orderType === "INDOOR" || !!newOrderTable;

  const isFirstRender = useRef(true);

  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] =
    useState(false);
  const [discountsSelected, setDiscountsSelected] = useState(discounts);
  const [showAddDiscountModal, setShowAddDiscountModal] = useState(false);
  const [
    showConfirmDeletePaymentMethodModal,
    setShowConfirmDeletePaymentMethodModal,
  ] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(false);
  const [amountToPay, setAmountToPay] = useState(totalToPay || 0);
  const [showServiceFeeDialog, setShowServiceFeeDialog] = useState(false);

  // Calculate payment percentage and completion
  const paidPercentage =
    totalToPay > 0
      ? Math.min(
          100,
          Math.max(0, ((totalToPay - amountToPay) / totalToPay) * 100)
        )
      : 0;
  const isFullyPaid = amountToPay <= 0;
  const canSubmit = isFullyPaid || (isTableOrder && paymentMethods.length === 0);

  useEffect(() => {
    if (!totalToPay) {
      return;
    }

    if (!paymentMethods || paymentMethods.length === 0) {
      setAmountToPay(totalToPay);
      return;
    }

    const methodsAmount = paymentMethods.reduce(
      (acc, method) =>
        acc + Number(parseFloatToFixed(method.amount || method.value)),
      0
    );
    setAmountToPay(totalToPay - methodsAmount);
  }, [totalToPay, paymentMethods]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (allowDiscountEdit && discountsSelected.length > 0) {
      dispatch(setPaymentMethods([]));
    }
  }, [discountsSelected, allowDiscountEdit, dispatch]);

  const handleAddPaymentMethod = (paymentMethod) => {
    const { icon, ...paymentMethodWithoutIcon } = paymentMethod;
    const methodWithCorrectName = {
      ...paymentMethodWithoutIcon,
      name:
        paymentMethodWithoutIcon.name ||
        paymentMethodWithoutIcon.payment_method,
      payment_method:
        paymentMethodWithoutIcon.payment_method ||
        paymentMethodWithoutIcon.value,
    };
    dispatch(addPaymentMethod(methodWithCorrectName));
    setShowAddPaymentMethodModal(false);
  };

  const handleDeletePaymentMethodModal = (paymentMethod = null) => {
    setPaymentMethodToDelete(paymentMethod);
    setShowConfirmDeletePaymentMethodModal(
      !showConfirmDeletePaymentMethodModal
    );
  };

  const deletePaymentMethod = (paymentMethodId) => {
    dispatch(removePaymentMethod(paymentMethodId));
    setShowConfirmDeletePaymentMethodModal(false);
  };

  const handleAddPaymentMethodModal = () => {
    if (amountToPay <= 0) {
      dispatchAddAlert({ message: ORDER_WITH_FULL_PAYMENTS });
      return;
    }
    setShowAddPaymentMethodModal(!showAddPaymentMethodModal);
  };

  const handleAddDiscount = (discount) => {
    if (handleSetDiscounts) {
      handleSetDiscounts(discount);
    }
    setDiscountsSelected([...discountsSelected, discount]);
    handleAddDiscountModal();
  };

  const filterDiscounts = (discountId) => {
    const updatedDiscounts = discountsSelected.filter(
      (discount) => discount.id !== discountId
    );
    setDiscountsSelected(updatedDiscounts);
  };

  const handleAddDiscountModal = () => {
    setShowAddDiscountModal(!showAddDiscountModal);
  };

  const handleSetDiscounts = (discount) => {
    dispatch(addDiscount(discount));
  };

  const handleRemoveDiscounts = (removedDiscount) => {
    dispatch(removeDiscount(removedDiscount));
    filterDiscounts(removedDiscount);
  };

  const handleServiceFeeToggle = () => {
    setShowServiceFeeDialog(true);
  };

  const handleConfirmServiceFee = () => {
    dispatch(setApplyServiceFee(!applyServiceFee));
    setShowServiceFeeDialog(false);
  };

  const handleGoBack = () => {
    dispatch(setShowPaymentForm(false));
  };

  const handleCreateOrder = () => {
    if (onCreateOrder) {
      onCreateOrder();
    }
  };

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          pb: 14, // Space for sticky panel
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={handleGoBack}
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                bgcolor: "action.hover",
                borderColor: "primary.main",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            Pagamento
          </Typography>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ flexGrow: 1, overflow: "auto", px: 3 }}>
          {/* Service Fee Toggle Chip */}
          {serviceFee > 0 && (
            <Box sx={{ mb: 2 }}>
              <ServiceFeeToggleChip
                applied={applyServiceFee}
                onClick={handleServiceFeeToggle}
                label={`Taxa de Serviço: ${serviceFee}% ${
                  applyServiceFee
                    ? `(+${formatFinance(serviceFeeAmount)})`
                    : ""
                }`}
              />
            </Box>
          )}

          {/* Payment Methods Section */}
          <SectionContainer>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
            >
              Métodos de Pagamento
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: { xs: "nowrap", sm: "wrap" },
                overflowX: { xs: "auto", sm: "visible" },
                mt: 2,
                pb: { xs: 1, sm: 0 },
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f8fafc",
                  borderRadius: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#e2e8f0",
                  borderRadius: 3,
                  "&:hover": {
                    backgroundColor: "#cbd5e1",
                  },
                },
              }}
            >
              <AnimatePresence mode="popLayout">
                {paymentMethods.map((method) => (
                  <motion.div
                    key={method.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    style={{ display: "inline-flex" }}
                  >
                    <PaymentMethodChip
                      icon={getPaymentMethodIcon(
                        method.payment_method || method.value
                      )}
                      label={`${cleanSpecialCharacters(
                        method.name || method.payment_method
                      )} ${formatFinance(method.amount || parseFloat(method.value))}`}
                      onDelete={() => handleDeletePaymentMethodModal(method)}
                      sx={{ flexShrink: { xs: 0, sm: 1 } }}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
              <AddActionChip
                onClick={handleAddPaymentMethodModal}
                disabled={amountToPay <= 0}
                sx={{ flexShrink: 0 }}
              >
                <AddCircleIcon fontSize="small" />
                Adicionar
              </AddActionChip>
            </Box>
          </SectionContainer>

          {/* Discounts Section */}
          {allowDiscountEdit && (
            <SectionContainer>
              <Typography
                variant="h6"
                fontWeight={600}
                gutterBottom
                sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
              >
                Descontos
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: { xs: "nowrap", sm: "wrap" },
                  overflowX: { xs: "auto", sm: "visible" },
                  mt: 2,
                  pb: { xs: 1, sm: 0 },
                  "&::-webkit-scrollbar": {
                    height: 6,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f8fafc",
                    borderRadius: 3,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#e2e8f0",
                    borderRadius: 3,
                    "&:hover": {
                      backgroundColor: "#cbd5e1",
                    },
                  },
                }}
              >
                <AnimatePresence mode="popLayout">
                  {discountsSelected.map((discount) => (
                    <motion.div
                      key={discount.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2 }}
                      style={{ display: "inline-flex" }}
                    >
                      <DiscountChip
                        icon={<DiscountIcon />}
                        label={`${translateDiscountType(discount.type)} ${formatFinance(
                          discount.value
                        )}`}
                        onDelete={() => handleRemoveDiscounts(discount.id)}
                        sx={{ flexShrink: { xs: 0, sm: 1 } }}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
                <AddActionChip
                  onClick={handleAddDiscountModal}
                  sx={{ flexShrink: 0 }}
                >
                  <AddCircleIcon fontSize="small" />
                  Adicionar
                </AddActionChip>
              </Box>
            </SectionContainer>
          )}

          {/* Payment Breakdown Details (Expandable) */}
          <SectionContainer>
            <Accordion
              elevation={0}
              disableGutters
              sx={{
                "&:before": {
                  display: "none",
                },
                bgcolor: "transparent",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1" fontWeight={600}>
                  Ver Detalhes
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatFinance(itemsTotal)}
                    </Typography>
                  </Box>
                  {discountsValue > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Descontos
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="secondary"
                      >
                        -{formatFinance(discountsValue)}
                      </Typography>
                    </Box>
                  )}
                  {applyServiceFee && serviceFeeAmount > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Taxa de Serviço
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        +{formatFinance(serviceFeeAmount)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      Total
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {formatFinance(totalToPay)}
                    </Typography>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </SectionContainer>
        </Box>

        {/* Sticky Bottom Payment Summary Panel */}
        <PaymentSummaryPanel>
          <LinearPaymentProgress
            variant="determinate"
            value={canSubmit ? 100 : paidPercentage}
            sx={{
              mb: 2,
              "& .MuiLinearProgress-bar": {
                backgroundColor: canSubmit ? "success.main" : "primary.main",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                {isFullyPaid
                  ? "Pagamento Completo!"
                  : isTableOrder && paymentMethods.length === 0
                  ? "Pagar na mesa"
                  : `${Math.round(paidPercentage)}% pago`}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {isFullyPaid ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "success.main",
                    }}
                  >
                    <CheckCircleIcon />
                    R$ 0,00
                  </Box>
                ) : (
                  `Restante: ${formatFinance(Math.max(0, amountToPay))}`
                )}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              color={canSubmit ? "success" : "primary"}
              disabled={!canSubmit}
              onClick={handleCreateOrder}
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                height: 56,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              {canSubmit
                ? isTableOrder ? "Abrir Mesa" : "Criar Pedido"
                : "Pagamento Incompleto"}
            </Button>
          </Box>
        </PaymentSummaryPanel>

        {/* Service Fee Confirmation Dialog */}
        <Dialog
          open={showServiceFeeDialog}
          onClose={() => setShowServiceFeeDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              maxWidth: 400,
            },
          }}
        >
          <DialogTitle>
            {applyServiceFee
              ? "Remover Taxa de Serviço?"
              : "Aplicar Taxa de Serviço?"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {applyServiceFee
                ? `A taxa de ${serviceFee}% (${formatFinance(
                    serviceFeeAmount
                  )}) será removida do total.`
                : `Uma taxa de ${serviceFee}% será adicionada ao total.`}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setShowServiceFeeDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmServiceFee}
              color={applyServiceFee ? "error" : "primary"}
            >
              {applyServiceFee ? "Remover" : "Aplicar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modals */}
        {showAddPaymentMethodModal && (
          <AddPaymentMethodModal
            closeModal={handleAddPaymentMethodModal}
            showModal={showAddPaymentMethodModal}
            stillToBePaid={amountToPay}
            addPaymentMethod={handleAddPaymentMethod}
          />
        )}

        {showAddDiscountModal && (
          <AddDiscountModal
            closeModal={handleAddDiscountModal}
            showModal={showAddDiscountModal}
            addDiscount={handleAddDiscount}
            total={total}
          />
        )}

        {showConfirmDeletePaymentMethodModal && (
          <ConfirmModal
            show={showConfirmDeletePaymentMethodModal}
            label="Remover Método de Pagamento?"
            message={`Tem certeza que deseja remover o método de pagamento ${
              paymentMethodToDelete.payment_method || paymentMethodToDelete.name
            } no valor de ${formatFinance(
              parseFloatToFixed(
                paymentMethodToDelete.amount || paymentMethodToDelete.value
              )
            )}?`}
            action={() => deletePaymentMethod(paymentMethodToDelete.id)}
            closeModal={handleDeletePaymentMethodModal}
          />
        )}
      </Box>
    </Fade>
  );
}
